import type { DetailsData, ProductCardExtended, ProductSetItem, ProductUpsell } from '#root/api/clients/product/data-contracts'
import type { CartItem, PriceCart } from '#types/cart'
import type {
  InteractionOrigin,
  MonetateProductObject,
  P13nImpressionContext,
  ProductContext,
  ProductObject,
  SetItemObject,
  ShippingMethodContext,
  ShopTheLookContext
} from '#types/gtm'
import type { MonetateRecommendedProduct } from '#types/p13n'
import type { ProductAttribute } from '#types/product'
import { injectHead } from '#imports'

const gtmCartProductsMap = useLocalStorage('gtmCartProductsMap', {}, { mergeDefaults: true }).value
const gtmInteractionOrigin = useLocalStorage<InteractionOrigin>('gtmInteractionOrigin', '', { mergeDefaults: true })
let gtmInteractionForcedOrigin = false

export const interactionOriginMap: Record<InteractionOrigin, string> = {
  '': '',
  'search': 'Search',
  'grid': 'Grid',
  'jacket-finder': 'Jacket Finder',
  'backpack-finder': 'Backpack Finder',
  'footwear-finder': 'Footwear Finder',
  'explore-collection': 'Explore Collection',
  'product-bundles': 'Product Bundles',
  'shoe-finder-retail': 'Shoe Finder Retail',
  'product-compare': 'Product Compare',
  'tent-sleepingbag-guide': 'Tent and Sleepingbag Guide',
  'shop-the-look': 'Shop the Look',
  'direct-to-pdp': 'Direct to PDP',
  'recommendation-carousel': 'Product Recommendation Carousel',
  'up-sell-complete-the-look': 'UpSellCompleteTheLook',
  'customizer': 'Customizer',
  'fit-analytics': 'Fit Analytics'
}

export const getPageBreadcrumb = (crumbs) => {
  return crumbs?.map(({ label }) => label) || undefined
}

export const serializeAnalyticsBreadcrumbs = (crumbs) => crumbs.map(({ label }) => label).join(',')

export const deserializeAnalyticsBreadcrumbs = (serializedCrumbs) => serializedCrumbs?.split(',').map((label) => ({ label }))

export const getPageTitle = async () => {
  const titleTag = (await injectHead()?.resolveTags())?.find(({ tag }) => tag === 'title')
  return titleTag?.textContent || ''
}

const getColorAttributes = (product: CartItem) => {
  const defaultColor = getDefaultColorOption(product.attributes, product.id)
  const colorId = (defaultColor?.id || product.masterId)?.slice(-3)

  return {
    colorId,
    colorLabel: defaultColor?.label || product.colorDescription,
    id: defaultColor?.id
  }
}

export const getGTMProduct = (id: string): any => gtmCartProductsMap[id]

const findGTMProduct = (product: Record<any, any>): any => {
  return gtmCartProductsMap[product.masterId || product.id]
    || Object.values(gtmCartProductsMap).find(
      (item: any) =>
        (product.masterId && item.masterId === product.masterId) || (product.id && item.productId === product.id)
    )
}

export const getRecommendationListDescription = ({
  list,
  title,
}: {
  list: string
  title?: string
}): string => title ? `${list}: ${title}` : list

export const getListDescription = ({ breadcrumbs, searchTerm = '', appSource = '', productId = '' }): string => {
  const gtmProduct = findGTMProduct({ masterId: productId, id: productId })
  const interactionOrigin = appSource || gtmProduct?.interactionOrigin || gtmInteractionOrigin.value || 'direct-to-pdp'
  const isProductList = ['grid', 'search'].includes(interactionOrigin)
  const interactionTitle = interactionOriginMap[interactionOrigin]
  const pageBreadcrumbs = gtmProduct?.breadcrumbs?.map(({ label }) => label).join(' - ') || breadcrumbs
  const pageSearchTerm = gtmProduct?.searchTerm || searchTerm

  if (interactionOrigin === 'recommendation-carousel') return getRecommendationListDescription({ list: gtmProduct?.list })

  return `${interactionTitle}${isProductList ? `: PLP | ${pageSearchTerm || pageBreadcrumbs}` : ''}`
}

const getAvailableSizes = (attributes: ProductAttribute[]) => {
  const options = attributes?.find((attribute) => attribute.label === 'Size')?.options
  return options?.filter((size) => size.available).map((size) => size.label) || []
}

const getSelectedVariants = (attributes) => {
  let counter = 0
  return (Array.isArray(attributes) ? attributes : Object.values(attributes))
    .reduce((obj, { code, value }) => {
      if (code !== 'color')
        obj[`sizeCode${++counter}`] = value
      return obj
    }, { sizeCode1: '', sizeCode2: '', sizeCode3: '' })
}

const getDiscountTypesByPrice = (price: Partial<PriceCart>): string | undefined => {
  const discountTypes: string[] = []
  const current = price.proratedPrice === 0 ? 0 : (price.highCurrent || price.current || 0)
  const original = price.highOriginal || price.original || 0

  current === 0 && discountTypes.push('Free Gift')
  current > 0 && current < original && discountTypes.push('Marked Down')
  return discountTypes.length ? discountTypes.join(',') : undefined
}

// Add persistent storage to cart items to save context attributes needed for analytics
export const setGtmCartProductsMap = (id: string, product) => {
  const existingContext = findGTMProduct(product) as any

  const persistentValues = {
    productId: product.id,
    avgRating: product.rating?.score || 0,
    availSizes: getAvailableSizes(product.attributes),
    historicalCategory: history.state.category,
    lowOnStock: product.eyebrow?.id?.toUpperCase() === 'LOW STOCK',
    badge: product.badge?.label || product.eyebrow?.label,
    breadcrumbs: existingContext?.breadcrumbs
      || deserializeAnalyticsBreadcrumbs(history.state.breadcrumbs),
    productBreadcrumbs: product.breadcrumbs,
    numReviews: product.rating?.reviewsCount || 0,
    searchTerm: existingContext?.searchTerm || (history.state.searchTerm ?? history.state.suggestedPhrase),
    interactionOrigin: existingContext?.interactionOrigin || gtmInteractionOrigin.value,
    list: existingContext?.list || product.list
  }

  gtmCartProductsMap[id] = persistentValues
  gtmCartProductsMap[product.id] = persistentValues
}

export const setGtmCartProductVariant = (
  variantId: string,
  { appSource, category, list, stockStatus, stockQuantity }:
  { appSource?: InteractionOrigin, category?: string, list?: string, stockStatus?: string, stockQuantity?: number } = {}
) => {
  gtmCartProductsMap[variantId] = {
    ...gtmCartProductsMap[variantId],
    ...(appSource && { interactionOrigin: interactionOriginMap[appSource] }),
    ...(category && { category }),
    ...(list && { list }),
    ...(stockStatus && { stockStatus }),
    ...(stockQuantity && { stockQuantity })
  }
}

export const removeGtmCartProductsMap = (id: string, productId: string) => {
  delete gtmCartProductsMap[id]
  if (Object.values(gtmCartProductsMap).filter((gtmProduct: any) => gtmProduct.productId === productId).length === 1)
    delete gtmCartProductsMap[productId]
}

export const setInteractionOrigin = (interaction?: InteractionOrigin, force?: boolean) => {
  const historyState = history.state
  const productId = historyState?.pageKey
  const existingContext = productId && findGTMProduct({ masterId: productId, id: productId })
  gtmInteractionForcedOrigin = !!force

  gtmInteractionOrigin.value = existingContext?.interactionOrigin
    || ((historyState.searchTerm || historyState.breadcrumbs) && !force)
    ? historyState.searchTerm ? 'search' : 'grid'
    : interaction || 'direct-to-pdp'
}

export const resetInteractionOrigin = () => (gtmInteractionOrigin.value = '')
export const getInteractionOrigin = () => gtmInteractionOrigin.value

const getCustomItemsAttributes = (product) => {
  const { enableCustoms } = useFeatureFlags()
  const recipe = product.url && new URL(product.url, location.origin).searchParams.get('recipe')

  return enableCustoms
    ? {
        customized: !!recipe || isCustomsProduct(product) || !!product.recipeId,
        preCreatedCustomCode:
          ((recipe || product.dummyCustoms) && product.id)
          || product.precreatedCustomsCode
          || undefined,
        recipeID: recipe || product.customsRecipeID || product.recipeId || undefined
      }
    : {}
}

/*
  Due to specific financial reporting needs, "price" shall reflect prorated
  amounts which includes cart-level discounts. All other attributes shall
  only reflect product-level pricing and discounts (GLOBAL15-56246)
*/
const getCartProductObject = (product, context) => {
  const price = product.price
  const totalPrice = Math.min(price.rowTotal, price.priceAfterItemDiscount, price.priceAfterOrderDiscount)
  const saleDiscountAmount: number = (price.original - price.current) * product.qty
  return {
    onSale: !!saleDiscountAmount,
    originalPrice: +product.price.original,
    price: (totalPrice - product.taxTotal) / product.qty,
    quantity: context.quantity || product.qty,
    is_prime: product.isPrimeIntended || false,
    saleDiscountAmount,
    taxTotal: product.taxTotal,
    vf_itemID: product.productId,
    virtualStyleCode: product.masterId || product.id,
    ...getSelectedVariants(product.variants),
    ...getCustomItemsAttributes(product)
  }
}

const getProductObject = (product, context) => ({
  avgRating: product.rating?.score || 0,
  availSizes: getAvailableSizes(product.attributes),
  numReviews: product.rating?.reviewsCount || 0,
  onSale: product.price.highCurrent !== product.price.highOriginal,
  originalPrice: +product.price.highOriginal,
  price: +product.price.highCurrent,
  saleDiscountAmount: +(product.price.highOriginal - product.price.highCurrent).toFixed(2),
  searchTerm: context.searchTerm,
  ...getCustomItemsAttributes(product)
})

const getGtmProductBrand = () => `${useRuntimeConfig().public.brand}-${useCountryCode()}`.toUpperCase()

const getShippingTrack = (product: CartItem):
  (Omit<ShippingMethodContext, 'shippingWindow'> & { shippingWindow: string }) | undefined => {
  const shipping = product.shippingOptions?.find(({ selected }) => selected)
  if (!shipping) return
  const shippingGTM = {
    shippingStoreId: undefined,
    shippingWindow: shipping.shippingMethod.deliveryTime,
  }
  if (isPickupOrSts(shipping.shippingMethod.code)) {
    return {
      ...shippingGTM,
      shippingMethod: shipping.shippingMethod.code === 'sts'
        ? 'BOPIS_STS'
        : 'BOPIS_SD',
      shippingStoreId: shipping.storeInfo?.id,
    }
  }

  if (shipping.shippingMethod.code === 'prime') {
    return {
      ...shippingGTM,
      shippingMethod: 'PRIME'
    }
  }
  return {
    ...shippingGTM,
    shippingMethod: 'STH',
  }
}

const getGtmCartProduct = (productData = {}) => {
  return Object.entries(productData).reduce((product, [key, value]) => {
    if (value) product[key] = value
    return product
  }, {})
}

export const getProductObjectList
  = (products: CartItem[] | ProductCardExtended[] | DetailsData[], context: ProductContext) => {
    return products?.map((product, i) => {
      const trace = JSON.parse(sessionStorage.gtmTrace || '{}')

      const pid = product.productId || product.id
      const gtmCartProduct = findGTMProduct({ id: pid })
      const isStyle = PRODUCT_STYLE_ID_REGEX.test(pid)
      const colorAttributes = getColorAttributes(product)

      const pageBreadcrumbs = getPageBreadcrumb(context?.breadcrumbs || gtmCartProduct?.breadcrumbs || []).join(' - ')
      const productBreadcrumbs
        = getPageBreadcrumb(product.breadcrumbs || gtmCartProduct?.productBreadcrumbs) || []

      const searchTerm = context.searchTerm || gtmCartProduct?.searchTerm
      const interactionOrigin = interactionOriginMap[gtmCartProduct?.interactionOrigin || gtmInteractionOrigin.value || 'direct-to-pdp']

      const catalogCategory = productBreadcrumbs.at(-1)
      const forcedCategory = gtmInteractionForcedOrigin ? interactionOrigin : null

      const list = context.list || getListDescription({
        breadcrumbs: pageBreadcrumbs,
        searchTerm,
        appSource: gtmCartProduct?.interactionOrigin,
        productId: pid
      })

      const recommendationCategory = gtmCartProduct?.interactionOrigin === 'recommendation-carousel' && list

      const category = gtmCartProduct?.category
        || recommendationCategory
        || forcedCategory
        || context?.enCategoryNameWithId
        || context.category
        || interactionOrigin
        || pageBreadcrumbs
        || gtmCartProduct?.historicalCategory

      const { styleId, hasProductSeason } = useAppConfig().components.gtm
      const getProductId = () => {
        let id = product.id || product.masterId
        if (isStyle) id = colorAttributes?.id
        else if (context.isCartItem) id = product.precreatedCustomsCode || product.masterId || product.id
        return id
      }

      return {
        badge: product.badge?.label || product.eyebrow?.label,
        bundleId: context.bundleId,
        brand: getGtmProductBrand(),
        colorCode: colorAttributes?.colorId,
        colorDescription: colorAttributes?.colorLabel,
        coupon: product.productPromotions?.map(({ couponCode, promotionId }) => couponCode || promotionId).join('|'),
        couponDiscountAmount: product.productPromotions?.reduce((acc, { price }) => acc + Math.abs(price), 0),
        couponType: product.productPromotions?.map(({ couponCode }) => couponCode ? 'CODE' : 'AUTO').join('|'),
        productPromoCampaignId: product.productPromotions
          ?.map(({ campaignId }) => campaignId)
          .filter(Boolean)
          .join('|'),
        onCoupon: !!product.productPromotions,
        discountType: getDiscountTypesByPrice(product.price),
        id: getProductId(),
        lowOnStock: product.eyebrow?.id?.toUpperCase() === 'LOW STOCK',
        name: product.name.trim(),
        productNameEng: product.productNameEng?.trim(),
        styleCode: (product.masterId || product.id)?.match(styleId)?.[1],
        list,
        category,
        catalogCategory,
        searchTerm,
        stockStatus: context.stockStatus,
        ...(hasProductSeason && product.season ? { season: product.season } : {}),
        ...(!context.omitPosition ? { position: (context.offset || 0) + i + 1 } : {}),
        ...(context.isCartItem
          ? getCartProductObject(product, context)
          : getProductObject(product, context)),
        ...getGtmCartProduct(gtmCartProductsMap[pid]),
        ...getShippingTrack(product),
        ...trace[pid]
      }
    }) || []
  }

export const getProductRecommendationObject = (
  product: MonetateRecommendedProduct,
  position: number,
  context?: Partial<P13nImpressionContext>
): MonetateProductObject => {
  const list = getRecommendationListDescription({ list: context?.list || '', title: context?.title })

  return {
    id: product.itemGroupId || product.id,
    name: product.title,
    avgRating: Number.parseFloat(product.stars || '0'),
    brand: getGtmProductBrand(),
    colorCode: product.id.split(':')[2], // That's the best we can do with Monetate data
    list,
    category: list,
    onSale: product.price !== product.salePrice,
    originalPrice: product.price,
    position,
    price: product.salePrice,
    saleDiscountAmount: product.price - product.salePrice,
    ...getCustomItemsAttributes(product),
    ...(context?.experience
      ? {
          experience_id: context.experience.id,
          experience_label: context.experience.label,
          experience_name: context.experience.name,
          recs_set: context.experience.component,
          recs_set_id: context.experience.actionId
        }
      : {})
  }
}

export const getProductSetItemObjectList = (
  products: ProductSetItem[],
  { productSet }: ShopTheLookContext
): SetItemObject[] => products.map((setItem) => ({
  id: setItem.id,
  name: setItem.name,
  category: 'Shop the Look',
  list: 'Shop the Look',
  bundleId: productSet.productSetId,
  position: productSet.products!.indexOf(setItem) + 1
}))

export const getProductUpsellObject = (
  upsell: ProductUpsell,
  position: number
): Partial<ProductObject> => {
  const splitSku = upsell.sku.split(':')
  const masterId = splitSku[0] + splitSku[1]
  return {
    avgRating: undefined,
    badge: undefined,
    brand: getGtmProductBrand(),
    bundleId: undefined,
    catalogCategory: undefined,
    category: 'UpSellCompleteTheLook',
    colorCode: splitSku[2],
    colorDescription: undefined,
    discountType: getDiscountTypesByPrice(upsell.price),
    id: upsell.id,
    list: 'UpSellCompleteTheLook',
    name: upsell.name,
    numReviews: undefined,
    onSale: upsell.price.highCurrent !== upsell.price.highOriginal,
    originalPrice: upsell.price.highOriginal,
    position,
    price: upsell.price.highCurrent,
    saleDiscountAmount: +(upsell.price.highOriginal - upsell.price.highCurrent).toFixed(2),
    searchTerm: undefined,
    styleCode: masterId
  }
}

export const formatTrackingCategory = (name: string | undefined, url?: string | undefined) =>
  `${name?.toLowerCase().trim().replace(/\s+/g, '-')}_${url?.match(/\d+$/)?.[0] || ''}`
